import { db } from './config';
import {
  collection,
  doc,
  setDoc,
  query,
  orderBy,
  getDocs,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import type { Appointment, AppointmentHistoryEntry } from '@/types/models';

/** Campos rastreados pelo histórico. Mudanças em outros campos não geram
 *  entrada (notes, reminderSent, cobrança — esta última tem audit próprio).
 *  Manter a lista pequena evita ruído na timeline. */
const TRACKED_FIELDS: ReadonlyArray<keyof Appointment> = [
  'status',
  'startTime',
  'endTime',
  'date',
  'professionalId',
  'type',
  'appointmentTypeId',
  'insurerId',
  'equipmentId',
  'cancellationReason',
  // Troca do paciente ocupante do horário (reatribuição pelo quick panel).
  // Rastreado por nome (legível no histórico); o `patientId` também é gravado.
  'patientName',
];

const computeDiff = (
  before: Partial<Appointment>,
  after: Partial<Appointment>,
): Record<string, { from?: unknown; to?: unknown }> => {
  const diff: Record<string, { from?: unknown; to?: unknown }> = {};
  for (const f of TRACKED_FIELDS) {
    if (!(f in after)) continue;
    const from = before[f];
    const to = after[f];
    if (from === to) continue;
    diff[f as string] = { from, to };
  }
  return diff;
};

export const AppointmentHistoryService = {
  /** Lista o histórico de um agendamento em ordem cronológica descendente
   *  (mais recente primeiro). */
  async list(clinicId: string, appointmentId: string): Promise<AppointmentHistoryEntry[]> {
    try {
      const q = query(
        collection(db, 'clinics', clinicId, 'appointments', appointmentId, 'history'),
        orderBy('at', 'desc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => d.data() as AppointmentHistoryEntry);
    } catch (err) {
      console.error('[appointment-history] list failed:', err);
      return [];
    }
  },

  /** Grava manualmente uma entrada de histórico (sem disparar update do
   *  agendamento). Usado por fluxos que aplicam a mudança via batch
   *  separado (ex: confirmação em lote). */
  async record(
    clinicId: string,
    appointmentId: string,
    entry: Omit<AppointmentHistoryEntry, 'id' | 'clinicId' | 'appointmentId' | 'at'>,
  ): Promise<void> {
    const ref = doc(collection(db, 'clinics', clinicId, 'appointments', appointmentId, 'history'));
    const raw: Record<string, unknown> = {
      ...entry,
      id: ref.id,
      clinicId,
      appointmentId,
      at: Date.now(),
    };
    const clean = Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== undefined),
    );
    await setDoc(ref, clean);
  },

  /**
   * Atualiza o agendamento e grava uma entrada no histórico contendo só
   * os campos rastreados que mudaram. Use no lugar de
   * `AppointmentService.updateAppointment` quando a mudança tiver
   * relevância pra auditoria (status, horário, profissional, etc.).
   *
   * Se nenhum campo rastreado mudou (ex: só alterou notes), pula a
   * gravação do histórico e atualiza o doc direto — sem ruído.
   */
  async updateTracked(
    clinicId: string,
    appointmentId: string,
    patch: Partial<Appointment>,
    actor: { uid: string; name?: string; action?: string },
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const before = snap.data() as Appointment;
    const diff = computeDiff(before, patch);

    // Atualiza o doc (sempre — diff vazio pode ser update de campo não
    // rastreado, como `notes` ou flags de aprovação).
    const rawUpdate: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() };
    const cleanUpdate = Object.fromEntries(
      Object.entries(rawUpdate).filter(([, v]) => v !== undefined),
    );
    await updateDoc(ref, cleanUpdate);

    // Só grava history se algo rastreado mudou.
    if (Object.keys(diff).length > 0) {
      await this.record(clinicId, appointmentId, {
        changes: diff,
        action: actor.action,
        actorUid: actor.uid,
        actorName: actor.name,
      });
    }
  },
};
